/**
 * Client-side computation of rolling percentile threshold series.
 * Mirrors the logic in funding-rate-spike-v2.ts:435-447 so the paper-trading
 * chart can display the same dynamic threshold lines as the backtest chart.
 */

export interface FundingRateRecord {
  timestamp: number;
  fundingRate: number;
}

export interface IndicatorSeries {
  timestamps: number[];
  values: number[];
}

/**
 * Same percentile algorithm as the strategy (funding-rate-spike-v2.ts:52-56).
 * p is in 0-100. Sorts ascending, picks floor((n*p)/100), clamped to [0, n-1].
 */
function calcPercentile(arr: number[], p: number): number {
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = Math.floor((sorted.length * p) / 100);
  return sorted[Math.min(idx, sorted.length - 1)];
}

/**
 * For each FR observation at index i (0-based), computes the rolling percentile
 * over the window of FR values ending at index i, restricted to the last `lookback`
 * observations. Returns an IndicatorSeries with one entry per input rate starting
 * from index `minHistory - 1`. Earlier bars are skipped (warmup period where the
 * strategy would not enter anyway).
 *
 * Mirrors the strategy logic:
 *   const lookbackRates = recentRates.slice(-percentileLookback).map(r => r.fundingRate);
 *   if (lookbackRates.length < 10) return;
 *   shortThreshold = calcPercentile(lookbackRates, shortPercentile_);
 */
export function computePercentileThresholdSeries(
  rates: FundingRateRecord[],
  percentile: number,
  lookback: number,
  minHistory: number = 10,
): IndicatorSeries {
  if (rates.length === 0) return { timestamps: [], values: [] };

  const timestamps: number[] = [];
  const values: number[] = [];

  for (let i = 0; i < rates.length; i++) {
    // How many rates are available up to and including index i
    const available = i + 1;
    if (available < minHistory) continue;

    // Take only the last `lookback` observations (mirrors slice(-percentileLookback))
    const windowStart = Math.max(0, i + 1 - lookback);
    const window = rates.slice(windowStart, i + 1).map((r) => r.fundingRate);

    timestamps.push(rates[i].timestamp);
    values.push(calcPercentile(window, percentile));
  }

  return { timestamps, values };
}
